import type * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // Original shadcn variants (kept for backward compatibility)
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 focus-visible:ring-primary",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive",
        outline:
          "border border-input bg-white text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 focus-visible:ring-secondary",
        ghost: "hover:bg-muted hover:text-foreground focus-visible:ring-muted",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80 focus-visible:ring-primary",
        
        // New design system variants
        primary: "bg-[#7C12C1] text-white hover:bg-[#6A0FA8] focus-visible:ring-[#7C12C1]",
        white: "bg-white border border-[#E5E5E5] text-[#1A1A1A] hover:bg-[#FAFAFA] focus-visible:ring-[#E5E5E5]",
        outlinePrimary: "bg-white border border-[#7C12C1] text-[#7C12C1] hover:bg-[#F5EBFF] focus-visible:ring-[#7C12C1]",
        outlineDestructive: "bg-white border border-[#EF4444] text-[#EF4444] hover:bg-[rgba(239,68,68,0.05)] focus-visible:ring-[#EF4444]",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 rounded-lg px-6 has-[>svg]:px-4",
        icon: "size-10 p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
